/**
 * Reusable phone → OTP login, parametrized by role. Both the Vendor and
 * Pet-Owner login screens render this with different copy + post-login
 * destination. Test ids are stable across roles so e2e specs stay simple.
 *
 * Layout: a single clay card split into a welcome panel (accent gradient, ≥ lg)
 * and the auth panel. On mobile only the auth panel shows, led by a compact
 * brand header. Matches the landing page's tasteful, friendly tone.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, Loader2, Moon, PawPrint, ShieldCheck, Sun } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ApiError } from '@/lib/api';
import { use_session_store, type SessionRole } from '@/lib/session-store';
import { use_theme_store } from '@/lib/theme-store';
import { send_otp, verify_otp } from '@/features/auth/api';

type Step = 'phone' | 'otp';

/**
 * Fallback only. The send-OTP response carries the authoritative
 * `cooldown_seconds` (server-side `OTP_COOLDOWN_SECONDS`); always prefer it, so
 * the button can't re-enable before the server will accept another send.
 */
const RESEND_COOLDOWN_FALLBACK_SECONDS = 60;

/**
 * The backend returns `attempts_remaining` alongside an OTP_INVALID so the user
 * knows how close they are to the 5-attempt lockout. Fold it into the message.
 */
function verify_error_message(err: ApiError): string {
  const remaining = (err.details as { attempts_remaining?: number } | undefined)
    ?.attempts_remaining;
  if (typeof remaining !== 'number') return err.message;
  if (remaining <= 0) return `${err.message} — no attempts left, request a new code.`;
  return `${err.message} — ${remaining} attempt${remaining === 1 ? '' : 's'} left.`;
}

const WELCOME: Record<SessionRole, { headline: string; sub: string; perks: string[] }> = {
  vendor: {
    headline: 'Grow your pet-care business',
    sub: 'List your services, manage availability, and fill your calendar with happy clients.',
    perks: [
      'Guided onboarding & KYC',
      'Manage services & availability',
      'Track every booking in one place',
    ],
  },
  user: {
    headline: 'Care for your pet, in a tail-wag',
    sub: 'Book trusted vets, walkers, grooming and more — all in one friendly place.',
    perks: [
      'Profiles for all your pets',
      'Book vets, walkers, grooming & more',
      'Reminders so you never miss care',
    ],
  },
};

export function OtpLogin({
  role,
  title,
  subtitle,
  success_path,
  resolve_success_path,
}: {
  role: SessionRole;
  title: string;
  subtitle: string;
  success_path: string;
  /** Optional: compute the post-login destination once the session token is set
   *  (e.g. send already-submitted vendors straight to their dashboard). Falls
   *  back to `success_path` if it throws. */
  resolve_success_path?: () => Promise<string>;
}) {
  const navigate = useNavigate();
  const set_session = use_session_store((s) => s.set_session);
  const dark = use_theme_store((s) => s.dark);
  const toggle_theme = use_theme_store((s) => s.toggle);

  const [step, set_step] = useState<Step>('phone');
  const [phone, set_phone] = useState('');
  const [normalized_phone, set_normalized_phone] = useState<string | null>(null);
  const [debug_otp_code, set_debug_otp_code] = useState<string | null>(null);
  const [code, set_code] = useState('');
  const [error, set_error] = useState<string | null>(null);
  const [loading, set_loading] = useState(false);
  /** Seconds until "Resend code" re-enables; 0 means it's available now. */
  const [resend_in, set_resend_in] = useState(0);

  const welcome = WELCOME[role];

  useEffect(() => {
    if (resend_in <= 0) return;
    const timer = setTimeout(() => set_resend_in((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resend_in]);

  async function handle_send_otp(e: React.FormEvent) {
    e.preventDefault();
    set_error(null);
    if (!/^\d{10}$/.test(phone.replace(/\D/g, ''))) {
      set_error('Enter a 10-digit Indian mobile number');
      return;
    }
    set_loading(true);
    try {
      const res = await send_otp(role, phone);
      set_normalized_phone(res.phone);
      set_debug_otp_code(res.debug_otp_code ?? null);
      if (res.debug_otp_code) set_code(res.debug_otp_code); // dev convenience
      set_resend_in(res.cooldown_seconds ?? RESEND_COOLDOWN_FALLBACK_SECONDS);
      set_step('otp');
    } catch (err) {
      set_error(err instanceof ApiError ? err.message : 'Failed to send OTP');
    } finally {
      set_loading(false);
    }
  }

  /**
   * Re-request a code for the same number. Without this the OTP step is a
   * dead end once the code expires (5 min) or locks after 5 failed attempts.
   */
  async function handle_resend_otp() {
    if (resend_in > 0 || loading) return;
    set_error(null);
    set_loading(true);
    try {
      const res = await send_otp(role, phone);
      set_normalized_phone(res.phone);
      set_debug_otp_code(res.debug_otp_code ?? null);
      set_code(res.debug_otp_code ?? '');
      set_resend_in(res.cooldown_seconds ?? RESEND_COOLDOWN_FALLBACK_SECONDS);
    } catch (err) {
      set_error(err instanceof ApiError ? err.message : 'Failed to resend OTP');
    } finally {
      set_loading(false);
    }
  }

  async function handle_verify_otp(e: React.FormEvent) {
    e.preventDefault();
    set_error(null);
    if (!/^\d{6}$/.test(code)) {
      set_error('Enter the 6-digit code');
      return;
    }
    if (!normalized_phone) {
      set_error('Lost session state — please request a new OTP');
      set_step('phone');
      return;
    }
    set_loading(true);
    try {
      const res = await verify_otp(role, normalized_phone, code);
      set_session({ onboarding_token: res.onboarding_token, phone: res.phone, role });
      // The session token is now in the store, so any resolver request is authed.
      let destination = success_path;
      if (resolve_success_path) {
        try {
          destination = await resolve_success_path();
        } catch {
          destination = success_path;
        }
      }
      navigate(destination);
    } catch (err) {
      set_error(err instanceof ApiError ? verify_error_message(err) : 'Verification failed');
    } finally {
      set_loading(false);
    }
  }

  function handle_back_to_phone() {
    set_step('phone');
    set_code('');
    set_debug_otp_code(null);
    set_error(null);
    set_resend_in(0);
  }

  return (
    <div className="relative flex min-h-svh items-center justify-center overflow-hidden p-5">
      {/* Soft decorative blobs (match the landing hero) */}
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -left-24 -top-20 h-72 w-72 rounded-full bg-accent/15 blur-3xl" />
        <div className="absolute -bottom-24 right-0 h-80 w-80 rounded-full bg-primary/15 blur-3xl" />
      </div>

      <Button
        variant="ghost"
        size="sm"
        className="absolute left-4 top-4 gap-1.5"
        onClick={() => navigate('/')}
        data-testid="back-to-chooser"
      >
        <ArrowLeft className="h-4 w-4" /> Back
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="absolute right-4 top-4"
        onClick={toggle_theme}
        aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
        data-testid="theme-toggle"
      >
        {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
      </Button>

      <div className="grid w-full max-w-4xl overflow-hidden rounded-card border-2 border-brand-outline-variant/20 bg-card shadow-clay-lg lg:grid-cols-2">
        {/* Welcome panel (desktop) */}
        <aside className="relative hidden flex-col justify-between overflow-hidden accent-gradient p-9 text-white lg:flex">
          <div aria-hidden className="pointer-events-none absolute inset-0 opacity-15">
            <PawPrint className="absolute -right-4 top-8 h-28 w-28 rotate-12" />
            <PawPrint className="absolute -left-6 bottom-10 h-20 w-20 -rotate-12" />
          </div>
          <div className="relative flex items-center gap-2.5">
            <span className="flex h-10 w-10 items-center justify-center rounded-button bg-white/20 backdrop-blur-sm">
              <PawPrint className="h-5 w-5" />
            </span>
            <span className="font-heading text-lg font-bold tracking-tight">Pet Marketplace</span>
          </div>
          <div className="relative">
            <h2 className="font-heading text-3xl font-extrabold leading-tight tracking-tight">
              {welcome.headline}
            </h2>
            <p className="mt-3 max-w-xs text-white/90">{welcome.sub}</p>
            <ul className="mt-6 space-y-3">
              {welcome.perks.map((perk) => (
                <li key={perk} className="flex items-center gap-2.5 text-sm font-medium">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white/20">
                    <Check className="h-3.5 w-3.5" strokeWidth={3} />
                  </span>
                  {perk}
                </li>
              ))}
            </ul>
          </div>
          <p className="relative text-xs text-white/75">
            Trusted by 1,200+ pet parents &amp; 350+ vendors
          </p>
        </aside>

        {/* Auth panel */}
        <div className="flex flex-col justify-center p-6 sm:p-9">
          <div className="mb-6 flex flex-col items-center gap-3 text-center lg:items-start lg:text-left">
            <div className="flex h-12 w-12 items-center justify-center rounded-button accent-gradient text-white shadow-clay-sm lg:hidden">
              <PawPrint className="h-6 w-6" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold tracking-tight text-foreground">
                {title}
              </h1>
              <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
            </div>
          </div>

          <div className="mb-5">
            <h2 className="font-heading text-lg font-bold">
              {step === 'phone' ? 'Sign in with your phone' : 'Enter the OTP'}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {step === 'phone'
                ? "We'll send a one-time code to verify your number."
                : `Sent to ${normalized_phone}. Code expires in 5 minutes.`}
            </p>
          </div>

          {step === 'phone' ? (
            <form onSubmit={handle_send_otp} className="flex flex-col gap-5">
              <div className="flex flex-col gap-2">
                <Label htmlFor="phone">Mobile number</Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="9876543210"
                  value={phone}
                  onChange={(e) => set_phone(e.target.value)}
                  autoComplete="tel"
                  inputMode="numeric"
                  maxLength={15}
                  required
                  data-testid="phone-input"
                />
              </div>
              {error && (
                <div
                  className="rounded-md border border-destructive/20 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                  data-testid="login-error"
                  role="alert"
                >
                  {error}
                </div>
              )}
              <Button
                type="submit"
                disabled={loading}
                variant="gradient"
                className="w-full"
                data-testid="send-otp-button"
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                {loading ? 'Sending OTP…' : 'Send OTP'}
              </Button>
            </form>
          ) : (
            <form onSubmit={handle_verify_otp} className="flex flex-col gap-5">
              <div className="flex flex-col gap-2">
                <Label htmlFor="otp">6-digit code</Label>
                <Input
                  id="otp"
                  type="text"
                  placeholder="123456"
                  value={code}
                  onChange={(e) => set_code(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  required
                  data-testid="otp-input"
                />
                {debug_otp_code && (
                  <div
                    className="flex items-center gap-2 rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-xs text-warning"
                    data-testid="debug-otp-banner"
                  >
                    <ShieldCheck className="h-4 w-4 shrink-0" />
                    <span>
                      <strong>Dev mode:</strong> auto-filled code{' '}
                      <code className="font-mono">{debug_otp_code}</code>. In prod, this is delivered
                      via SMS.
                    </span>
                  </div>
                )}
              </div>
              {error && (
                <div
                  className="rounded-md border border-destructive/20 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                  data-testid="login-error"
                  role="alert"
                >
                  {error}
                </div>
              )}
              <Button
                type="submit"
                disabled={loading}
                variant="gradient"
                className="w-full"
                data-testid="verify-otp-button"
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                {loading ? 'Verifying…' : 'Verify & continue'}
              </Button>
              <div className="flex flex-col items-center gap-2">
                <button
                  type="button"
                  onClick={handle_resend_otp}
                  disabled={resend_in > 0 || loading}
                  className="text-center text-xs font-semibold text-primary hover:underline disabled:cursor-not-allowed disabled:text-muted-foreground disabled:no-underline"
                  data-testid="resend-otp-button"
                >
                  {resend_in > 0 ? `Resend code in ${resend_in}s` : 'Resend code'}
                </button>
                <button
                  type="button"
                  onClick={handle_back_to_phone}
                  className="text-center text-xs font-semibold text-muted-foreground hover:text-foreground"
                  data-testid="change-phone-button"
                >
                  Use a different number
                </button>
              </div>
            </form>
          )}

          <p className="mt-6 flex items-center justify-center gap-1.5 text-center text-xs text-muted-foreground lg:justify-start">
            <ShieldCheck className="h-3.5 w-3.5 shrink-0" />
            Sandbox: every action hits the real backend. Use a test phone.
          </p>
        </div>
      </div>
    </div>
  );
}
